import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback
} from 'react'
import { isEmpty } from 'ramda'
import styled from 'styled-components'
import {
  Box,
  Flex,
  FormField,
  Label,
  Select,
  getPaletteColor,
  Text
} from 'pcln-design-system'
import { useRouter } from 'next/router'
import { Calendar } from '@pcln/date-picker'
import pclnExperimentation from '@pcln/experimentation'
import { AirPricedCalendar } from '@pcln/fly-date-picker'
import { useForm } from 'react-hook-form'
import CalendarWrapperBox from '@/components/CalenderWrapperBox.styled'
import DateField from '@/components/DateField'
import ShadowEffect from '@/components/ShadowEffect'
import TypeAhead from '@/components/TypeAhead'
import { PopoverContainer } from '@/components/Popover'
import { FLIGHTS_DISCLAIMER } from '@/components/TravelerSelection/constants'
import TravelerSelection from '@/components/TravelerSelection/TravelerSelection'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import useTabGroup from '@/hooks/useTabGroup'
import useSeti from '@/hooks/useSeti'
import SearchFormButton from '@/components/SearchFormButton'
import { getDateRange } from '@/shared-utils/date-helper'
import isMoreThanFourPerRoom from '@/shared-utils/packages-room-count'
import {
  getDateFieldBorderColors,
  getDateFieldErrorMessage
} from '@/shared-utils/error-helpers'
import { LOCATION_SEARCH_TYPE } from '@/types'
import BundleAndSave from '@/components/BundleAndSave/BundleAndSave'
import analytics from '@/shared-utils/analytics'
import {
  ANALYTICS_ACTION_FLIGHTS_SEARCH,
  ANALYTICS_CATEGORIES,
  INVALID
} from '@/constants/analytics'
import AmbiguousCityLink from '@/components/AmbiguousCityLink'
import MultiDestination from './MultiDestination'
import {
  ADD_PRODUCT_OPTIONS,
  CABIN_CLASS,
  FLIGHT_TYPE_INFO,
  HOTEL,
  MULTI_DESTINATION,
  ONE_WAY,
  ROUND_TRIP
} from '../constants'
import {
  FlightRecordType,
  FlightFormStateType,
  CABIN_CLASS_TYPE,
  FLIGHT_TYPE_INFO as FLIGHT_TYPE_INFO_TYPE,
  FlightDateType
} from '../types'
import searchFormSchema from './validation'
import FlightTypeRadios from './FlightTypeRadios'
import {
  setFlights,
  getPrevDate,
  isRoundTrip,
  isOneWay,
  isMultiDestination,
  includesStay,
  isPackage,
  fields,
  initialState,
  flightsStartLocationSearchKey,
  flightsEndLocationSearchKey,
  flightsDateRangeKey
} from './utils'

const StyledSelect = styled<{ isMobile: boolean }>(Select)`
  background-color: ${getPaletteColor('background.lightest')};
  cursor: pointer;
  height: ${props => (props.isMobile ? undefined : '56px')};
`

const StyledButtonContainer = styled(Box)`
  position: relative;
`

type FormType = {
  onSubmit: (data: FlightFormStateType) => void
  flightTypes?: FLIGHT_TYPE_INFO_TYPE[]
  promoDeal?: boolean
}

export default function Form({
  onSubmit,
  flightTypes = [
    FLIGHT_TYPE_INFO[ROUND_TRIP],
    FLIGHT_TYPE_INFO[ONE_WAY],
    FLIGHT_TYPE_INFO[MULTI_DESTINATION]
  ],
  promoDeal
}: FormType) {
  const { registerTabElement, focusNextElement } = useTabGroup()

  const { isMobile, prePopData, webstats, customer } =
    useContext(BootstrapDataContext)

  const isMoreRoomCapacityAllowed =
    useSeti('PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION', false) ===
    'VARIANT'

  const {
    flights,
    flightType,
    cabinClass,
    adultCount,
    youthCount,
    childrenCount,
    infantCount
  } = prePopData?.flights ?? {}

  const initialValue: FlightFormStateType = {
    ...initialState,
    ...(flights && { flights }),
    ...(flightType && { flightType }),
    ...(cabinClass && { cabinClass }),
    ...(adultCount && { adultCount }),
    ...(youthCount && { youthCount }),
    ...(childrenCount && { childrenCount }),
    ...(infantCount && { infantCount })
  }

  const {
    errors,
    handleSubmit,
    register,
    setValue,
    triggerValidation,
    unregister,
    watch
  } = useForm<FlightFormStateType>({
    defaultValues: initialValue,
    mode: 'onSubmit',
    validationSchema: searchFormSchema,
    reValidateMode: 'onChange',
    validationContext: {
      isMoreRoomCapacityAllowed
    }
  })
  const {
    adultCount: watchAdultCount,
    cabinClass: watchCabinClass,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    infantCount: watchInfantCount,
    flightType: watchFlightType,
    flights: watchFlights,
    roomCount: watchRoomCount,
    tripType: watchTripType,
    youthCount: watchYouthCount
  } = watch({ nest: true })

  const { query, pathname } = useRouter()
  const {
    'ambig-origin-city': ambigOriginCity,
    'ambig-dest-city': ambigDestCity
  } = query
  const isHomepage = pathname === '/'
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]

  const flightButtonSetiEnabled =
    useSeti('HP_FLIGHTS_BUTTON_PLACEMENT') === 'VARIANT'

  useEffect(() => {
    fields.forEach(name => {
      register({ name })
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [register, unregister])

  useEffect(() => {
    triggerValidation('roomCount')
  }, [
    triggerValidation,
    watchAdultCount,
    watchChildrenCount,
    watchInfantCount,
    watchTripType,
    watchRoomCount,
    watchYouthCount
  ])
  // PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION
  useEffect(() => {
    if (
      watchTripType.includes(HOTEL) &&
      isMoreThanFourPerRoom(
        watchRoomCount,
        watchAdultCount +
          watchYouthCount +
          watchChildrenCount +
          watchInfantCount
      )
    ) {
      pclnExperimentation.fireImpression(
        'PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION'
      )
    }
  }, [
    watchTripType,
    watchRoomCount,
    watchAdultCount,
    watchYouthCount,
    watchChildrenCount,
    watchInfantCount
  ])

  useEffect(() => {
    if (!isEmpty(errors)) {
      triggerValidation('flights')

      let gaLabel = `${INVALID} -`
      if (errors.flights) {
        errors.flights.forEach((flight, index) => {
          const invalidFlightFields = Object.keys(flight)
          gaLabel += ` Flight ${index + 1}: ${invalidFlightFields.join('| ')}`
        })
      }
      if (errors.roomCount) gaLabel += ' | roomCount'
      if (errors.infantCount) gaLabel += ' | infantCount'

      analytics.fireEvent({
        action: ANALYTICS_ACTION_FLIGHTS_SEARCH,
        category: ANALYTICS_CATEGORY,
        label: gaLabel
      })
    }
  }, [errors, triggerValidation, watchFlights, ANALYTICS_CATEGORY])

  useEffect(() => {
    triggerValidation('infantCount')
  }, [
    triggerValidation,
    watchFlights,
    watchInfantCount,
    watchAdultCount,
    watchChildrenAges
  ])

  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)
  const [departureAirport, setDepartureAirport] =
    useState<LOCATION_SEARCH_TYPE | null>(null)
  const [arrivalAirport, setArrivalAirport] =
    useState<LOCATION_SEARCH_TYPE | null>(null)
  const calendarInput = useRef<HTMLInputElement | null>(null)
  const startLocationPlaceholder = 'Departing from?'
  const endLocationPlaceholder = 'Going to?'
  const isFlightTypeRoundTrip = isRoundTrip(watchFlightType)
  const isFlightTypeOneWay = isOneWay(watchFlightType)
  const isFlightTypeMultiDestination = isMultiDestination(watchFlightType)
  const isFlightTypeIncludeStay = includesStay(watchFlightType)
  const showCabinClass =
    (!isFlightTypeRoundTrip || !isPackage(watchTripType)) && !promoDeal
  const showPackages = isFlightTypeRoundTrip && !promoDeal
  const firstFlight: Partial<FlightRecordType> = watchFlights[0] || {}
  const datePickerLabel = isFlightTypeRoundTrip
    ? 'Departing - Returning'
    : 'Departing'
  const datePickerAriaLabel =
    isFlightTypeOneWay || isFlightTypeMultiDestination
      ? 'Departing Date Picker'
      : 'Departing – Returning Date Picker'

  const isPricedCalendarActive = !isFlightTypeMultiDestination

  const CalendarComponent = isPricedCalendarActive
    ? AirPricedCalendar
    : Calendar

  const focusCalendarInput = (): void => {
    if (calendarInput && calendarInput.current) {
      calendarInput.current.focus()
    }
  }

  const showPackageOptions = () =>
    showPackages && (
      <BundleAndSave
        category={ANALYTICS_CATEGORY}
        productOptions={ADD_PRODUCT_OPTIONS}
        tripType={watchTripType}
        label="Bundle + Save"
        setValue={setValue}
      />
    )

  const [showOriginMoreCityLink, setShowOriginMoreCityLink] = useState(true)
  const origin = firstFlight?.startLocation?.cityName
  const showOriginDisambiguation =
    !!ambigOriginCity && !!origin && !isHomepage && showOriginMoreCityLink

  const ambiguousOriginElement = useRef<HTMLInputElement>()
  function registerOriginAmbiguousElement(ref: HTMLInputElement): void {
    ambiguousOriginElement.current = ref
  }

  const [showDestMoreCityLink, setShowDestMoreCityLink] = useState(true)
  const destination = firstFlight?.endLocation?.cityName
  const showDestDisambiguation =
    !!ambigDestCity && destination && !isHomepage && showDestMoreCityLink

  const ambiguousDestElement = useRef<HTMLInputElement>()
  function registerDestAmbiguousElement(ref: HTMLInputElement): void {
    ambiguousDestElement.current = ref
  }

  const clarifyAmbiguity = useCallback(
    (
      element: React.MutableRefObject<HTMLInputElement | undefined>,
      flight: Partial<FlightRecordType>,
      locationType: 'endLocation' | 'startLocation'
    ) => {
      const location = flight[locationType]

      return setTimeout(() => {
        if (element.current && location) {
          element.current.focus()
          setValue('flights', [
            {
              ...flight,
              [locationType]: {
                ...location,
                itemName: location.cityName
              }
            }
          ])
        }
      }, 0)
    },
    [setValue]
  )
  const typeaheadWidth = [1, null, 1 / 2, null, 1 / 3]
  const searchButtonContainerWidth = [
    1,
    null,
    showCabinClass ? 1 : 1 / 2,
    null,
    showCabinClass ? 1 / 3 : 1 / 2
  ]
  if (flightButtonSetiEnabled && !isFlightTypeMultiDestination) {
    typeaheadWidth[4] = 1 / 2
    searchButtonContainerWidth[4] = 1 / 2
  }
  return (
    <form
      onSubmit={handleSubmit(values =>
        onSubmit({ ...values, gaCategory: ANALYTICS_CATEGORY })
      )}
      data-testid={promoDeal ? 'express-deal-form' : 'form'}
      aria-label={
        promoDeal ? 'flight-express-deal-search-form' : 'flight-search-form'
      }
    >
      <Flex mb={2}>
        <FlightTypeRadios
          flightTypesToDisplay={flightTypes}
          flightType={watchFlightType}
          setValue={setValue}
          register={register}
          flights={watchFlights}
          isExpressDeal={!!promoDeal}
        />
      </Flex>
      <Flex key={0} mx={-2} flexWrap="wrap">
        <Box width={typeaheadWidth} p={2}>
          <ShadowEffect enabled forProperty={flightsStartLocationSearchKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAhead
                searchProduct="flights"
                placeholder={startLocationPlaceholder}
                label={startLocationPlaceholder}
                searchKey={flightsStartLocationSearchKey(0)}
                errors={errors}
                defaultSelectedItem={firstFlight?.startLocation ?? {}}
                disableShadowState={disableShadowState}
                ref={(elem: HTMLInputElement) => {
                  registerTabElement(elem)
                  if (showOriginDisambiguation) {
                    registerOriginAmbiguousElement(elem)
                  }
                }}
                onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                  setFlights(
                    watchFlights,
                    {
                      startLocation: item
                    },
                    setValue
                  )
                  setDepartureAirport(item)
                  if (item?.cityName !== origin) {
                    setShowOriginMoreCityLink(false)
                  }
                  if (!isMobile && !!item) {
                    focusNextElement()
                  }
                }}
              />
            )}
          </ShadowEffect>
          {showOriginDisambiguation && (
            <AmbiguousCityLink
              city={origin}
              handleCallback={() => {
                clarifyAmbiguity(
                  ambiguousOriginElement,
                  firstFlight,
                  'startLocation'
                )
                analytics.fireEvent({
                  action: 'ambiguous-city',
                  category: ANALYTICS_CATEGORY,
                  label: `${origin} - flight origin`
                })
              }}
            />
          )}
        </Box>
        <Box width={typeaheadWidth} p={2}>
          <ShadowEffect enabled forProperty={flightsEndLocationSearchKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAhead
                searchProduct="flights"
                placeholder={endLocationPlaceholder}
                label={endLocationPlaceholder}
                searchKey={flightsEndLocationSearchKey(0)}
                errors={errors}
                defaultSelectedItem={firstFlight?.endLocation ?? {}}
                disableShadowState={disableShadowState}
                ref={(elem: HTMLInputElement) => {
                  registerTabElement(elem)
                  if (showDestDisambiguation) {
                    registerDestAmbiguousElement(elem)
                  }
                }}
                onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                  setFlights(
                    watchFlights,
                    {
                      endLocation: item
                    },
                    setValue
                  )
                  setArrivalAirport(item)
                  if (item?.cityName !== destination) {
                    setShowDestMoreCityLink(false)
                  }
                  if (!isMobile && !!item) {
                    focusNextElement()
                  }
                }}
              />
            )}
          </ShadowEffect>
          {showDestDisambiguation && (
            <AmbiguousCityLink
              city={destination}
              handleCallback={() => {
                clarifyAmbiguity(
                  ambiguousDestElement,
                  firstFlight,
                  'endLocation'
                )
                analytics.fireEvent({
                  action: 'ambiguous-city',
                  category: ANALYTICS_CATEGORY,
                  label: `${destination} - flight destination`
                })
              }}
            />
          )}
        </Box>
        <Flex
          alignItems="baseline"
          justifyContent="stretch"
          width={[1, null, null, null, 1 / 3]}
          px={2}
          py={2}
        >
          <ShadowEffect forProperty={flightsDateRangeKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <PopoverContainer
                key="dates"
                data-calendar
                open={isCalendarOpen}
                position={promoDeal ? 'left' : 'right'}
                onDismiss={() => {
                  setIsCalendarOpen(false)
                  disableShadowState()
                }}
              >
                <Box>
                  <DateField
                    m={0}
                    width={1 / 2}
                    id={flightsDateRangeKey(0)}
                    name={flightsDateRangeKey(0)}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                      calendarInput.current = elem
                    }}
                    label={datePickerLabel}
                    placeholder={datePickerLabel}
                    value={getDateRange(
                      {
                        departureDate: firstFlight?.startDate ?? null,
                        arrivalDate: firstFlight?.endDate ?? null
                      },
                      !isFlightTypeRoundTrip
                    )}
                    onFocus={() => {
                      setIsCalendarOpen(true)
                      // analytics call for handleCalendarOnFocus(`datefield ${i}`)
                    }}
                    error={getDateFieldErrorMessage(
                      errors,
                      0,
                      isFlightTypeRoundTrip
                    )}
                    errorZIndex={1}
                    hideTooltip={
                      isCalendarOpen ||
                      ((isFlightTypeOneWay || isFlightTypeMultiDestination) &&
                        !!firstFlight.startDate) ||
                      (isFlightTypeRoundTrip &&
                        !!firstFlight.startDate &&
                        !!firstFlight.endDate)
                    }
                    isMobile={isMobile}
                    color={getDateFieldBorderColors(errors, 0)}
                    data-testid={`FLIGHTS_TRAVEL_DATES_INPUT_${0}`}
                    aria-label={datePickerAriaLabel}
                    aria-expanded={isCalendarOpen ? 'true' : 'false'}
                  />
                </Box>
                <CalendarWrapperBox>
                  <CalendarComponent
                    monthWidthPx={300}
                    id={`flight-date-range-calendar-${0}`}
                    onChange={(
                      _e: React.ChangeEvent,
                      dates: FlightDateType
                    ) => {
                      const { startDate = '', endDate = '' } = dates
                      const dateObj = {
                        startDate,
                        ...(isFlightTypeRoundTrip && { endDate })
                      }
                      setFlights(watchFlights, dateObj, setValue)
                      if (
                        !isFlightTypeRoundTrip ||
                        (dates.startDate && dates.endDate)
                      ) {
                        disableShadowState()
                        setIsCalendarOpen(false)
                      }
                    }}
                    animateScroll
                    minDate={getPrevDate(watchFlights, 0)}
                    startDate={firstFlight.startDate}
                    endDate={
                      isFlightTypeRoundTrip ? firstFlight.endDate : undefined
                    }
                    selectTwoDates={isFlightTypeRoundTrip}
                    allowSameDay={!isFlightTypeIncludeStay}
                    flightDetails={{
                      cabinClass: watchCabinClass,
                      flights: [
                        {
                          departureAirport,
                          arrivalAirport
                        }
                      ],
                      tripType: watchFlightType
                    }}
                    variantName="EXPRESS"
                    useDefaultFooter={isFlightTypeMultiDestination}
                    onDismiss={() => {
                      disableShadowState()
                      setIsCalendarOpen(false)
                      focusCalendarInput()
                    }}
                    useColorCode={!isFlightTypeMultiDestination}
                    callFlexDatesGraphUrl
                  />
                </CalendarWrapperBox>
              </PopoverContainer>
            )}
          </ShadowEffect>
        </Flex>
        {isFlightTypeMultiDestination && (
          <Box p={2} width={1}>
            <MultiDestination
              flights={watchFlights}
              setValue={setValue}
              errors={errors}
              isMobile={isMobile}
            />
          </Box>
        )}
        <Box
          width={[1, null, 1 / 2, null, showCabinClass ? 1 / 3 : 1 / 2]}
          p={2}
        >
          <ShadowEffect
            enabled
            forProperty="travelers-selection-readonly-input"
            alternativeBlurHandling
          >
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TravelerSelection
                adultCount={watchAdultCount}
                childrenAges={watchChildrenAges}
                childrenCount={watchChildrenCount}
                disableShadowState={disableShadowState}
                disclaimer={FLIGHTS_DISCLAIMER}
                infantCount={watchInfantCount}
                isMobile={isMobile}
                onDoneButtonClick={disableShadowState}
                roomCount={watchRoomCount}
                setValue={setValue}
                showChildAgeSelectors={false}
                showInfantMenuItem
                showRoomMenuItem={watchTripType.includes(HOTEL)}
                showSubLabel
                showYouthMenuItem
                youthCount={watchYouthCount}
                error={
                  errors?.infantCount?.message ||
                  errors?.roomCount?.message ||
                  ''
                }
              />
            )}
          </ShadowEffect>
        </Box>
        {showCabinClass && (
          <Box width={[1, null, 1 / 2, null, 1 / 3]} p={2}>
            <ShadowEffect forProperty="cabin-class-select">
              {(
                { disableShadowState }: { disableShadowState: () => void } // eslint-disable-line react/no-unused-prop-types
              ) => (
                <FormField>
                  <Label autoHide htmlFor="cabin-class-select">
                    Cabin Class
                  </Label>
                  <StyledSelect
                    isMobile={isMobile}
                    m={0}
                    id="cabin-class-select"
                    name="cabinClass"
                    value={watchCabinClass}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setValue('cabinClass', e.target.value as CABIN_CLASS_TYPE)
                      disableShadowState()
                      /**  for GA tracking */
                    }}
                  >
                    {Object.keys(CABIN_CLASS).map(name => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </StyledSelect>
                </FormField>
              )}
            </ShadowEffect>
          </Box>
        )}
        {isMobile && (
          <Box width={1} p={2}>
            {showPackageOptions()}
          </Box>
        )}
        {!isMobile && !isFlightTypeMultiDestination && flightButtonSetiEnabled && (
          <Flex width={1 / 2} alignItems="center">
            <Box height="50%" mt={-2} mb={3}>
              {showPackageOptions()}
            </Box>
          </Flex>
        )}

        <StyledButtonContainer width={searchButtonContainerWidth} p={2}>
          <SearchFormButton
            buttonText="Find Your Flight"
            data-testid="FLIGHTS_SUBMIT_BUTTON"
            handleClick={() => {
              if (isMobile) {
                window?.PCLN?.pclnEventDispatcher?.dispatch('FIND_YOUR_FLIGHT')
              }
            }}
            data-rudder-event
            data-sub-type="search"
            data-source={isHomepage ? 'homepage' : 'flight landing'}
            data-parent-product="FLY"
            data-child-product={watchTripType.split(':').slice(1).join(':')}
            data-cguid={webstats.clientGUID}
            data-email-address={customer.emailAddress}
          />
          <Box
            style={{
              position:
                !isMobile && isFlightTypeRoundTrip && !flightButtonSetiEnabled
                  ? 'absolute'
                  : undefined
            }}
            pb={isMobile ? 3 : undefined}
          >
            <Text
              fontSize={0}
              align={
                flightButtonSetiEnabled && !isFlightTypeMultiDestination
                  ? 'center'
                  : 'left'
              }
              mt={isMobile ? '12px' : 2}
            >
              Book a flight with free cancellation for flexibility
            </Text>
          </Box>
        </StyledButtonContainer>
        {!isMobile &&
          !isFlightTypeMultiDestination &&
          !flightButtonSetiEnabled && (
            <Flex width={1 / 2} alignItems="center">
              <Box mt={2} mb={2}>
                {showPackageOptions()}
              </Box>
            </Flex>
          )}
      </Flex>
    </form>
  )
}
